import { supabase } from '@/integrations/supabase/client';

export type PublicBookingPhoneVerificationErrorCode =
  | 'internal_access_invalid'
  | 'tenant_access_required'
  | 'profile_not_found'
  | 'public_booking_phone_verification_invalid'
  | 'public_booking_phone_verification_already_verified'
  | 'public_booking_phone_verification_pending_required'
  | 'public_booking_phone_verification_challenge_not_found'
  | 'public_booking_phone_verification_delivery_target_missing'
  | 'public_booking_phone_verification_rate_limited'
  | 'public_booking_phone_verification_retry_cooldown'
  | 'public_booking_phone_verification_delivery_failed';

export class PublicBookingPhoneVerificationError extends Error {
  code: PublicBookingPhoneVerificationErrorCode;
  details?: string | null;
  hint?: string | null;

  constructor(
    code: PublicBookingPhoneVerificationErrorCode,
    message: string,
    options?: { details?: string | null; hint?: string | null },
  ) {
    super(message);
    this.name = 'PublicBookingPhoneVerificationError';
    this.code = code;
    this.details = options?.details ?? null;
    this.hint = options?.hint ?? null;
  }
}

export interface PublicBookingPhoneVerificationState {
  profilePhone: string | null;
  securityPhoneVerified: string | null;
  securityPhoneVerifiedMasked: string | null;
  securityPhoneVerifiedAt: string | null;
  securityPhonePending: string | null;
  securityPhonePendingMasked: string | null;
  securityPhonePendingRequestedAt: string | null;
  securityPhoneChangedAt: string | null;
  hasVerifiedSecurityPhone: boolean;
  hasPendingSecurityPhone: boolean;
  canStartVerification: boolean;
  canVerifyPending: boolean;
  openChallengePublicId: string | null;
  openChallengeExpiresAt: string | null;
  openChallengeRemainingAttempts: number | null;
  challengeDeliveryChannel: 'whatsapp';
  challengeDeliveryTargetMasked: string | null;
  openChallengeSendCount: number | null;
  openChallengeLastSentAt: string | null;
  openChallengeNextSendAllowedAt: string | null;
  openChallengeRateLimitResetAt: string | null;
}

export interface PublicBookingPhoneVerificationDeliveryResponse {
  challengePublicId: string;
  status: 'pending' | 'verified' | 'cancelled';
  expiresAt: string;
  remainingAttempts: number;
  deliveryChannel: 'whatsapp';
  deliveryTargetMasked: string | null;
  pendingPhoneMasked: string | null;
  hasVerifiedSecurityPhone: boolean;
  verifiedPhoneMasked: string | null;
  wasCreated: boolean;
  sendCount: number;
  resendAvailableAt: string | null;
  rateLimitResetAt: string | null;
}

export interface VerifyPublicBookingPhoneVerificationResult {
  challengePublicId: string;
  status: 'pending' | 'verified' | 'cancelled';
  verifiedAt: string | null;
  attemptCount: number;
  remainingAttempts: number;
  securityPhoneVerified: string | null;
  securityPhoneVerifiedMasked: string | null;
  wasVerifiedNow: boolean;
}

type RpcStateRow = {
  profile_phone: string | null;
  security_phone_verified: string | null;
  security_phone_verified_masked: string | null;
  security_phone_verified_at: string | null;
  security_phone_pending: string | null;
  security_phone_pending_masked: string | null;
  security_phone_pending_requested_at: string | null;
  security_phone_changed_at: string | null;
  has_verified_security_phone: boolean;
  has_pending_security_phone: boolean;
  can_start_verification: boolean;
  can_verify_pending: boolean;
  open_challenge_public_id: string | null;
  open_challenge_expires_at: string | null;
  open_challenge_remaining_attempts: number | null;
  challenge_delivery_channel: 'whatsapp';
  challenge_delivery_target_masked: string | null;
  open_challenge_send_count: number | null;
  open_challenge_last_sent_at: string | null;
  open_challenge_next_send_allowed_at: string | null;
  open_challenge_rate_limit_reset_at: string | null;
};

type RpcVerifyRow = {
  challenge_public_id: string;
  status: 'pending' | 'verified' | 'cancelled';
  verified_at: string | null;
  attempt_count: number;
  remaining_attempts: number;
  security_phone_verified: string | null;
  security_phone_verified_masked: string | null;
  was_verified_now: boolean;
};

function firstRow<T>(value: T[] | T | null): T | null {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }

  return value ?? null;
}

function mapStateRow(row: RpcStateRow): PublicBookingPhoneVerificationState {
  return {
    profilePhone: row.profile_phone,
    securityPhoneVerified: row.security_phone_verified,
    securityPhoneVerifiedMasked: row.security_phone_verified_masked,
    securityPhoneVerifiedAt: row.security_phone_verified_at,
    securityPhonePending: row.security_phone_pending,
    securityPhonePendingMasked: row.security_phone_pending_masked,
    securityPhonePendingRequestedAt: row.security_phone_pending_requested_at,
    securityPhoneChangedAt: row.security_phone_changed_at,
    hasVerifiedSecurityPhone: row.has_verified_security_phone,
    hasPendingSecurityPhone: row.has_pending_security_phone,
    canStartVerification: row.can_start_verification,
    canVerifyPending: row.can_verify_pending,
    openChallengePublicId: row.open_challenge_public_id,
    openChallengeExpiresAt: row.open_challenge_expires_at,
    openChallengeRemainingAttempts: row.open_challenge_remaining_attempts,
    challengeDeliveryChannel: row.challenge_delivery_channel,
    challengeDeliveryTargetMasked: row.challenge_delivery_target_masked,
    openChallengeSendCount: row.open_challenge_send_count,
    openChallengeLastSentAt: row.open_challenge_last_sent_at,
    openChallengeNextSendAllowedAt: row.open_challenge_next_send_allowed_at,
    openChallengeRateLimitResetAt: row.open_challenge_rate_limit_reset_at,
  };
}

function mapVerifyRow(row: RpcVerifyRow): VerifyPublicBookingPhoneVerificationResult {
  return {
    challengePublicId: row.challenge_public_id,
    status: row.status,
    verifiedAt: row.verified_at,
    attemptCount: row.attempt_count,
    remainingAttempts: row.remaining_attempts,
    securityPhoneVerified: row.security_phone_verified,
    securityPhoneVerifiedMasked: row.security_phone_verified_masked,
    wasVerifiedNow: row.was_verified_now,
  };
}

function normalizeRpcError(
  error: unknown): PublicBookingPhoneVerificationError {
  const message =
    error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
      ? error.message
      : 'internal_access_invalid';

  const details =
    error && typeof error === 'object' && 'details' in error && typeof error.details === 'string'
      ? error.details
      : null;

  const hint =
    error && typeof error === 'object' && 'hint' in error && typeof error.hint === 'string'
      ? error.hint
      : null;

  const code = message as PublicBookingPhoneVerificationErrorCode;

  return new PublicBookingPhoneVerificationError(code, message, {
    details,
    hint,
  });
}

async function normalizeFunctionError(error: unknown): Promise<PublicBookingPhoneVerificationError | Error> {
  const fallback = new Error('No pudimos enviar el código de verificación al teléfono pendiente.');

  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const maybeMessage = 'message' in error && typeof error.message === 'string' ? error.message : null;
  const maybeContext = 'context' in error ? error.context : null;

  if (maybeContext && typeof maybeContext === 'object' && 'json' in maybeContext && typeof maybeContext.json === 'function') {
    try {
      const payload = await maybeContext.json() as {
        error?: PublicBookingPhoneVerificationErrorCode;
        details?: string | null;
        hint?: string | null;
      };

      if (payload?.error) {
        return new PublicBookingPhoneVerificationError(payload.error, payload.error, {
          details: payload.details ?? null,
          hint: payload.hint ?? null,
        });
      }
    } catch {
      // ignore parse failures
    }
  }

  if (maybeMessage) {
    return new Error(maybeMessage);
  }

  return fallback;
}

export async function getMyPublicBookingPhoneVerificationState(): Promise<PublicBookingPhoneVerificationState> {
  const { data, error } = await supabase.rpc('rpc_get_my_public_booking_phone_verification_state_v1' as never);

  if (error) {
    throw normalizeRpcError(error);
  }

  const row = firstRow(data as RpcStateRow[] | RpcStateRow | null);
  if (!row) {
    throw new Error('El estado de verificación pública de teléfono no devolvió datos.');
  }

  return mapStateRow(row);
}

export async function requestPublicBookingPhoneVerificationDelivery(
  phone: string,
): Promise<PublicBookingPhoneVerificationDeliveryResponse> {
  const { data, error } = await supabase.functions.invoke<PublicBookingPhoneVerificationDeliveryResponse>(
    'public-booking-phone-verification-delivery',
    {
      body: { phone },
    },
  );

  if (error) {
    throw await normalizeFunctionError(error);
  }

  if (!data?.challengePublicId) {
    throw new Error('La solicitud del OTP público de booking no devolvió datos.');
  }

  return data;
}

export async function resendPublicBookingPhoneVerificationDelivery(): Promise<PublicBookingPhoneVerificationDeliveryResponse> {
  const { data, error } = await supabase.functions.invoke<PublicBookingPhoneVerificationDeliveryResponse>(
    'public-booking-phone-verification-delivery',
    {
      body: {},
    },
  );

  if (error) {
    throw await normalizeFunctionError(error);
  }

  if (!data?.challengePublicId) {
    throw new Error('El reenvío del OTP público de booking no devolvió datos.');
  }

  return data;
}

export async function verifyPublicBookingPhoneVerification(
  code: string,
): Promise<VerifyPublicBookingPhoneVerificationResult> {
  const { data, error } = await supabase.rpc('rpc_verify_my_public_booking_phone_verification_v1' as never, {
    p_code: code,
  } as never);

  if (error) {
    throw normalizeRpcError(error);
  }

  const row = firstRow(data as RpcVerifyRow[] | RpcVerifyRow | null);
  if (!row) {
    throw new Error('La verificación pública de teléfono no devolvió datos.');
  }

  return mapVerifyRow(row);
}